import React, { FC, useCallback, useState } from 'react';
import { LegacyTask } from 'src/interfaces/api/Collaborative';
import useCollaborative from 'src/hooks/api/useCollaborative';
import EntityTable from 'src/components/Table';
import { TableColumn } from 'src/components/Table/EntityTable';
import tasksState from 'src/recoil/atoms/tasks';
import { Order } from 'src/interfaces/Table';
import Ticket from 'src/interfaces/api/Ticket';
import data_criticity from 'src/data/criticity';
import data_priority from 'src/data/priority';
import { useSetRecoilState } from 'recoil';
import { ticketState } from 'src/recoil/selectors/tickets';
import useTicket from 'src/hooks/api/useTicket';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { IconButton } from '@material-ui/core';
import { MdDelete as DeleteIcon } from '@react-icons/all-files/md/MdDelete';

interface Props {
  hidden?: boolean;
  ticketId: Ticket['id'];
  tasksIds: LegacyTask['id'][];
}

const columns: TableColumn<LegacyTask>[] = [
  {
    id: 'title',
    type: 'text',
    label: 'Titre',
    link: (task: LegacyTask) => `/dashboard/task/${task.id}`,
  },
  {
    id: 'closed',
    type: 'text',
    label: 'Status',
    renderValue: (task) => (task.closed ? 'Clôs' : 'Ouvert'),
  },
  {
    id: 'deadline',
    type: 'datetime',
    label: 'Date limite',
    sortable: true,
  },
  {
    id: 'criticity',
    type: 'text',
    label: 'Criticité',
    sortable: true,
    renderValue: (task: LegacyTask) => data_criticity[task.criticity]?.fr,
  },
  {
    id: 'priority',
    type: 'text',
    label: 'Priorité',
    sortable: true,
    renderValue: (task: LegacyTask) => data_priority[task.priority]?.fr,
  },
  {
    id: 'dateCreate',
    type: 'datetime',
    label: 'Crée le',
    sortable: true,
  },
];

const TaskTable: FC<Props> = ({ ticketId, hidden, tasksIds }) => {
  const setTicket = useSetRecoilState(ticketState(ticketId));
  const { getTasks } = useCollaborative();
  const [taskToRemove, setTaskToRemove] = useState<LegacyTask['id']>();
  const { removeTask } = useTicket();

  const handleRemoveTask = async () => {
    if (taskToRemove) {
      const ticket = await removeTask(ticketId, taskToRemove);
      setTicket(ticket);
      setTaskToRemove(undefined);
    }
  };

  const handleGetTask = useCallback(
    async (page: number, limit: number, orderBy: keyof LegacyTask, order: Order) => {
      return await getTasks(tasksIds || [], page, limit, orderBy, order);
    },
    [tasksIds]
  );

  return (
    <>
      <EntityTable
        hidden={hidden}
        columns={columns}
        defaultOrderBy='dateCreate'
        atomState={tasksState}
        actions={(task) => (
          <IconButton size='small' onClick={() => setTaskToRemove(task.id)}>
            <DeleteIcon />
          </IconButton>
        )}
        fetchEntitiesCallback={handleGetTask}
      />

      <ConfirmationDialog
        title='Attention'
        confirmationText='Supprimer'
        description='Vous allez supprimer cette tâche. Êtes-vous sûr ?'
        onClose={() => setTaskToRemove(undefined)}
        open={!!taskToRemove}
        onValidate={handleRemoveTask}
      />
    </>
  );
};

export default React.memo(TaskTable, (prevProps, nextProps) => {
  return prevProps.tasksIds === nextProps.tasksIds && prevProps.hidden === nextProps.hidden;
});
